// Fixed code labels only. Never accept a URL, selector, or DOM text as a source.
export const APP_SCROLL_SOURCES = [
  'FEED_BOTTOM',
  'FEED_SEARCH',
  'FEED_PRESERVE',
  'FEED_WORKOUT_ITEM',
  'FEED_MEAL_ITEM',
  'FEED_RECIPE_ITEM',
  'SCHEDULE_MEAL_ITEM',
  'SCHEDULE_TODAY',
  'SCHEDULE_ANCHOR',
  'SCHEDULE_PREPEND_ANCHOR',
  'SCHEDULE_PREPEND_HEIGHT',
  'SCHEDULE_RESIZE',
  'NUTRITION_TODAY',
  'MEAL_HISTORY_BOTTOM',
  'MEAL_HISTORY_PRESERVE',
  'CLIENT_TAB',
  'CALL_EDITOR_WEIGHT',
  'CALL_EDITOR_VALIDATION',
  'WORKOUT_EDITOR',
  'WORKOUT_SECTION',
  'WORKOUT_EXERCISE',
  'WORKOUT_TEMPLATE',
  'AUTO_SCHEDULER_TOP',
  'AUTO_SCHEDULER_EDITOR',
  'ASSISTANT_BOTTOM',
  'SCROLL_MORE',
  'PAGE_CONTAINER_TOP',
  'PAGE_WINDOW_TOP',
  'DATE_SELECTOR',
] as const
export type AppScrollSource = (typeof APP_SCROLL_SOURCES)[number]
type Listener = (source: AppScrollSource, now: number) => void

// A shared page trace can have multiple mounted providers. Notify it once,
// and keep it subscribed until the last provider releases it.
const listeners = new Map<Listener, number>()
export const subscribeToAppScroll = (listener: Listener) => {
  listeners.set(listener, (listeners.get(listener) ?? 0) + 1)
  let subscribed = true
  return () => {
    if (!subscribed) return
    subscribed = false
    const remaining = (listeners.get(listener) ?? 1) - 1
    if (remaining) listeners.set(listener, remaining)
    else listeners.delete(listener)
  }
}

// Records an attempted app scroll, not proof that the browser moved or that a
// later scroll event came from this call. Preserve the action's return/errors.
export const trackAppScroll = <T>(
  source: AppScrollSource,
  action: () => T
): T => {
  const now = Date.now()
  for (const listener of listeners.keys()) {
    try {
      listener(source, now)
    } catch {
      // Diagnostics cannot prevent the original UI action.
    }
  }
  return action()
}

export const WORK_AREAS = [
  'UNKNOWN',
  'DASHBOARD',
  'CLIENT',
  'CLIENT_FEED',
  'CLIENT_SCHEDULE',
  'CLIENT_NUTRITION',
  'CALL_NOTES',
  'WORKOUT_EDITOR',
  'ASSISTANT',
] as const
export type WorkArea = (typeof WORK_AREAS)[number]
const areas = new Set<string>(WORK_AREAS)

// Inspect only our dedicated static marker. Do not read identifiers, classes,
// input values, text, selectors supplied by callers, or any URL.
export const scrollArea = (target: EventTarget | null): WorkArea => {
  if (target === document || target === window) return 'DASHBOARD'
  if (!(target instanceof Element)) return 'UNKNOWN'
  const label = target
    .closest('[data-work-time-area]')
    ?.getAttribute('data-work-time-area')
  return label && areas.has(label) ? (label as WorkArea) : 'UNKNOWN'
}

// pathname is Next's route template. Never include it or the router query in
// telemetry; map only to these constants.
export const pageArea = (pathname: string): WorkArea => {
  if (pathname === '/dashboard/[page]/[clientId]') return 'CLIENT'
  return pathname.startsWith('/dashboard') ? 'DASHBOARD' : 'UNKNOWN'
}
